import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

/**
 * Servicio para monitorear los mensajes de WhatsApp (cola de notificaciones).
 * Los endpoints responden { success, data }.
 */
class NotificationsService {
  constructor() {
    this.api = axios.create({
      baseURL: `${API_URL}/notifications`,
      headers: { 'Content-Type': 'application/json' }
    });

    // Interceptor para agregar token de autenticación
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('adminToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  // Listado paginado: devuelve { data, pagination }
  async getNotifications(params = {}) {
    try {
      const response = await this.api.get('/', { params });
      return response.data.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al cargar los mensajes'));
    }
  }

  // Conteos por estado de cola, estado de entrega y tipo (con los mismos filtros del listado)
  // más availableYears (global)
  async getStats(params = {}) {
    try {
      const response = await this.api.get('/stats', { params });
      return response.data.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al cargar estadísticas'));
    }
  }

  // Marca los mensajes fallidos para que la cola los reintente
  async retryFailed() {
    try {
      const response = await this.api.post('/retry-failed');
      return response.data.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al reintentar los mensajes fallidos'));
    }
  }

  // Un mensaje por id (se usa para ver el reemplazo de uno reenviado)
  async getNotification(id) {
    try {
      const response = await this.api.get(`/${id}`);
      return response.data.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al cargar el mensaje'));
    }
  }

  // Simulacro del reenvío de avisos que Meta bloqueó por ser marketing
  async previewMarketingBlockedResend() {
    try {
      const response = await this.api.get('/resend/marketing-blocked/preview');
      return response.data.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al calcular los reenvíos'));
    }
  }

  // Reenvía los bloqueados y cancela los originales
  async resendMarketingBlocked() {
    try {
      const response = await this.api.post('/resend/marketing-blocked');
      return response.data.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al reenviar los mensajes bloqueados'));
    }
  }

  // Alumnos y mensajes afectados por un número inalcanzable
  async previewPhoneCorrection(id) {
    try {
      const response = await this.api.get(`/${id}/phone-correction/preview`);
      return response.data.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al cargar la corrección de teléfono'));
    }
  }

  // Corrige el teléfono, reenvía los fallidos y cancela los originales
  async applyPhoneCorrection(id, phone) {
    try {
      const response = await this.api.post(`/${id}/phone-correction`, { phone });
      return response.data.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al corregir el teléfono'));
    }
  }

  // Manejar errores de API
  getErrorMessage(error, defaultMessage) {
    if (error.response) {
      const { status, data } = error.response;

      if (status === 401) {
        return 'No tienes autorización para realizar esta acción';
      }

      if (status === 403) {
        return 'Solo los administradores pueden ver los mensajes de WhatsApp';
      }

      if (data?.message) {
        return Array.isArray(data.message) ? data.message.join(', ') : data.message;
      }
    }

    if (error.request) {
      return 'No se pudo conectar con el servidor. Verifica tu conexión a internet.';
    }

    return defaultMessage;
  }
}

export const notificationsService = new NotificationsService();
export default notificationsService;
